/**
 * Lightweight CPP wizard — per-step Zod schemas.
 *
 * Lifted verbatim from
 * `apps/mobile/screens/wizards/WizardLightweightCppScreen.tsx`
 * (Phase 14). Lightweight CPP is the sole-trader / domestic-client
 * variant — it persists as `DocumentType.CPP` in Prisma but uses a
 * separate (shorter, free-text-first) wizard. See CLAUDE.md memory note
 * `project_lightweight_cpp`.
 *
 * Step order:
 *
 *   1. basics    — job description, address, client name, domestic confirm
 *   2. hazards   — free-text risk summary
 *   3. controls  — free-text control measures
 *   4. welfare   — free-text welfare arrangements
 *   5. emergency — fire + first aid + nearest hospital
 *   6. preview   — read-only summary (no validation)
 *
 * `preparedBy` is captured outside the wizard (session-derived) and so
 * isn't part of the per-step schemas — see the screen for how the
 * payload is assembled at submit time.
 */
import { z } from "zod";
import { nearestHospitalField } from "./nearest-hospital";

// ─── Per-step schemas ──────────────────────────────────────────────────────

export const LightweightCppBasicsStepSchema = z.object({
  jobDescription: z
    .string()
    .min(10, "Describe the job in at least 10 characters"),
  siteAddress: z.string().min(3, "Site address is required"),
  clientName: z.string().min(2, "Client name is required"),
  domesticConfirmed: z
    .boolean()
    .refine((v) => v === true, "Confirm this is a domestic project"),
});
export type LightweightCppBasicsStepData = z.infer<
  typeof LightweightCppBasicsStepSchema
>;

export const LightweightCppHazardsStepSchema = z.object({
  siteRisks: z
    .string()
    .min(10, "Describe the main risks in at least 10 characters"),
});
export type LightweightCppHazardsStepData = z.infer<
  typeof LightweightCppHazardsStepSchema
>;

export const LightweightCppControlsStepSchema = z.object({
  controlMeasures: z
    .string()
    .min(10, "Describe how the risks will be controlled"),
});
export type LightweightCppControlsStepData = z.infer<
  typeof LightweightCppControlsStepSchema
>;

export const LightweightCppWelfareStepSchema = z.object({
  welfareArrangements: z.string().min(5, "Describe welfare facilities"),
});
export type LightweightCppWelfareStepData = z.infer<
  typeof LightweightCppWelfareStepSchema
>;

export const LightweightCppEmergencyStepSchema = z.object({
  fireAndFirstAid: z
    .string()
    .min(10, "Describe fire & first aid arrangements"),
  nearestHospital: nearestHospitalField("Provide the nearest A&E hospital"),
});
export type LightweightCppEmergencyStepData = z.infer<
  typeof LightweightCppEmergencyStepSchema
>;

/** Preview is read-only — no validation. */
export const LightweightCppPreviewStepSchema = z.object({});
export type LightweightCppPreviewStepData = z.infer<
  typeof LightweightCppPreviewStepSchema
>;

// ─── Step ordering + composite ─────────────────────────────────────────────

export const LIGHTWEIGHT_CPP_WIZARD_STEPS = [
  { id: "basics", schema: LightweightCppBasicsStepSchema },
  { id: "hazards", schema: LightweightCppHazardsStepSchema },
  { id: "controls", schema: LightweightCppControlsStepSchema },
  { id: "welfare", schema: LightweightCppWelfareStepSchema },
  { id: "emergency", schema: LightweightCppEmergencyStepSchema },
  { id: "preview", schema: LightweightCppPreviewStepSchema },
] as const;

export const LightweightCppWizardFullSchema = z.object({
  ...LightweightCppBasicsStepSchema.shape,
  ...LightweightCppHazardsStepSchema.shape,
  ...LightweightCppControlsStepSchema.shape,
  ...LightweightCppWelfareStepSchema.shape,
  ...LightweightCppEmergencyStepSchema.shape,
});
export type LightweightCppWizardData = z.infer<
  typeof LightweightCppWizardFullSchema
>;
